import React, { useState, useMemo, useRef } from "react";
import { format, isSameDay, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { Row, Col, Card, CardTitle, CardBody } from "reactstrap";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import APLPresenceCheckTable from "./APLPresenceCheckTable";

const NUMBER_OF_WEEKS_TO_SHOW = 4;
const DATE_FORMAT_DAY_NAME = "EEE";
const DATE_FORMAT_DAY_NUMBER = "d";
const DATE_FORMAT_DAY_NUMBER_OF_WEEK = "i";
const DATE_FORMAT_WEEK_NUMBER = "I";
const DATE_FORMAT_YEAR = "yyyy";

const APLPresenceWeeksView = React.memo(function APLPresenceWeeksView({
  attendanceByWeek,
  studentId,
  objId,
  level,
  activePeriodId,
}) {
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDayData, setSelectedDayData] = useState(null);

  const today = useRef(new Date());

  const weekIntervals = useMemo(() => {
    const tempWeekIntervals = [];
    const currentWeekNumber = format(today.current, DATE_FORMAT_WEEK_NUMBER, {
      locale: sv,
    });
    const currentYear = format(today.current, DATE_FORMAT_YEAR, {
      locale: sv,
    });

    for (
      let i = 0;
      i < attendanceByWeek.data.weeks.length;
      i += NUMBER_OF_WEEKS_TO_SHOW
    ) {
      const chunk = attendanceByWeek.data.weeks.slice(
        i,
        i + NUMBER_OF_WEEKS_TO_SHOW
      );
      tempWeekIntervals.push(chunk);
    }
    let currentWeekChunk = 0;
    tempWeekIntervals.forEach((interval, i) => {
      if (
        interval.some(
          (weeks) =>
            weeks.weekNumber === Number(currentWeekNumber) &&
            weeks.year === Number(currentYear)
        )
      ) {
        currentWeekChunk = i;
      }
    });

    setCurrentWeekIndex(currentWeekChunk);
    return tempWeekIntervals;
  }, [attendanceByWeek.data.weeks]);

  const renderHeader = () => {
    const currentWeeks = weekIntervals[currentWeekIndex];
    if (!currentWeeks?.[0]) return null;

    const dateFormat = "MMM yyyy";
    const startOfCurrentTimeSpan = parseISO(
      currentWeeks[0]?.attendanceForStudents?.[0]?.day
    );
    const endOfCurrentTimeSpan = parseISO(
      currentWeeks.at(-1)?.attendanceForStudents?.at(-1)?.day
    );

    if (currentWeeks[0]) {
      return (
        <Row className="flex-middle mb-2" style={{ marginLeft: "-19px" }}>
          <Col className="col ps-1 lead">
            <span>
              {format(startOfCurrentTimeSpan, dateFormat, { locale: sv })} -{" "}
              {format(endOfCurrentTimeSpan, dateFormat, { locale: sv })}
            </span>
          </Col>
        </Row>
      );
    }
  };

  const cells = useMemo(() => {
    const result = [];
    const statusClassMap = {
      HadNoRegistrations: "no-registrations",
      WasPresent: "present",
      HadAbsence: "absent",
    };

    const attendanceData = weekIntervals[currentWeekIndex]?.flatMap((week) =>
      week.attendanceForStudents.filter(
        (record) => record.studentId === studentId
      )
    );

    attendanceData?.forEach((item) => {
      const parsedDay = parseISO(item.day);
      const formattedDayName = format(parsedDay, DATE_FORMAT_DAY_NAME, {
        locale: sv,
      });
      const formattedDayNumber = format(parsedDay, DATE_FORMAT_DAY_NUMBER, {
        locale: sv,
      });
      const formattedDayNumberOfWeek = format(
        parsedDay,
        DATE_FORMAT_DAY_NUMBER_OF_WEEK,
        { locale: sv }
      );
      const formattedWeekNumber = format(parsedDay, DATE_FORMAT_WEEK_NUMBER, {
        locale: sv,
      });

      const statusClass = item ? statusClassMap[item.status] || "" : "no-data";
      if (item && formattedDayNumberOfWeek < 6 && parsedDay <= today.current) {
        result.push({
          key: studentId + "" + item.day,
          parsedDay,
          formattedDayName,
          formattedDayNumber,
          formattedDayNumberOfWeek,
          formattedWeekNumber,
          statusClass,
          item,
        });
      }
    });

    return result;
  }, [weekIntervals, currentWeekIndex, studentId]);

  const renderDaysAndCells = () => {
    return (
      <>
        <div
          className="body d-flex calendar_days_row"
          style={{ overflowX: "auto", paddingTop: ".8em" }}
        >
          {currentWeekIndex > 0 ? (
            <div
              className="calendar_change_dateInterval_chevron calendar_change_dateInterval_chevron_left p-1 m-2"
              style={{
                alignSelf: "flex-end",
                zIndex: 2,
              }}
              onClick={() => changeWeekHandle("prev")}
            >
              <FontAwesomeIcon
                icon="chevron-left"
                style={{ marginRight: "0.1rem" }}
              />
            </div>
          ) : (
            <span style={{ width: "18px" }}></span>
          )}
          <>
            {cells.map((cell) => (
              <div
                className="col calendar_day_container px-0"
                key={cell.key}
              >
                {cell.formattedDayNumberOfWeek === "1" && (
                  <div
                    style={{
                      position: "absolute",
                      top: "2em",
                      width: "32px",
                      fontWeight: "bold",
                    }}
                  >
                    v. {cell.formattedWeekNumber}
                  </div>
                )}
                <div
                  className={
                    cell.formattedDayNumberOfWeek === "1"
                      ? "col calendar_dayname calendar_dayname_bold"
                      : "col calendar_dayname"
                  }
                >
                  {cell.formattedDayName}
                </div>
                <div
                  className={`col cell ${
                    isSameDay(cell.parsedDay, today.current) ? "today" : ""
                  } ${
                    isSameDay(cell.parsedDay, selectedDate) ? "selected" : ""
                  } ${cell.statusClass}`}
                  onClick={() => onDateClickHandle(cell.parsedDay, cell.item)}
                >
                  <span className="number">{cell.formattedDayNumber}</span>
                  <span className="bg" style={{ width: "100%" }}>
                    {cell.formattedDayNumber}
                  </span>
                </div>
              </div>
            ))}
          </>

          {currentWeekIndex + 1 < weekIntervals.length && (
            <div
              className="calendar_change_dateInterval_chevron calendar_change_dateInterval_chevron_right p-1 m-2"
              style={{ alignSelf: "flex-end", zIndex: 2 }}
              onClick={() => changeWeekHandle("next")}
            >
              <FontAwesomeIcon
                icon="chevron-right"
                style={{ marginRight: "0.1rem" }}
              />
            </div>
          )}
        </div>
      </>
    );
  };

  const onDateClickHandle = (day, attendanceRecord) => {
    setSelectedDate(day);
    setSelectedDayData(attendanceRecord);
  };

  const changeWeekHandle = (btnType) => {
    if (btnType === "prev") {
      setCurrentWeekIndex(currentWeekIndex - 1);
    } else if (btnType === "next") {
      setCurrentWeekIndex(currentWeekIndex + 1);
    }
  };

  const renderDetails = () => {
    if (selectedDayData?.day) {
      return (
        <Col lg="6">
          <Card className="mt-3" style={{ marginLeft: "-30px" }}>
            <CardTitle className="p-2 mb-0 d-flex justify-content-between">
              {moment(selectedDayData?.day).format("Do MMMM YYYY")}
              <span
                className="float-end px-2 -mt-2"
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedDayData(null)}
              >
                x
              </span>
            </CardTitle>
            <CardBody>
              <APLPresenceCheckTable
                presenceData={selectedDayData}
                absence={selectedDayData.absencesThisDay}
                periodId={activePeriodId}
                studentId={studentId}
                objId={objId}
                level={level}
              />
            </CardBody>
          </Card>
        </Col>
      );
    } else {
      return null;
    }
  };
  if (weekIntervals.length >= 1) {
    return (
      <div className="calendar px-4">
        {renderHeader()}
        {renderDaysAndCells()}
        {selectedDayData !== null && renderDetails()}
      </div>
    );
  } else {
    return null;
  }
});

export default APLPresenceWeeksView;
